import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["referenceIndicator", "referenceText", "referenceSize", "referenceTabIndex", "referenceStyles", "placement", "disabled", "menuInlineSize", "closeOnMenuClick", "open", "autoUpdateMenuPosition", "data-test-id", "onActionClick", "onOpenChange", "use", "shape", "children"],
  _excluded2 = ["aria-label", "aria-labelledby"];
import styled from 'foundations-theming/utils';
import { useRef, useState, Children, useCallback } from 'react';
import { useFloating, useListNavigation, useTypeahead, useClick, useDismiss, useRole, useInteractions, offset, flip, size, shift, hide, autoUpdate, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import unescapedText from 'I18n/utils/unescapedText';
import { DownCaratIcon } from 'foundations-assets';
import { Flex } from '../Flex/Flex';
import { useControlledOpen } from '../../hooks/useControlledOpen';
import { getReferenceLayout } from './ActionsMenu.helpers';
import { StyledContainer, StyledReferenceIndicator, StyledReferenceText, StyledReferenceWrapper, StyledMenuOptionsWrapper as StyledMenuOptionsWrapperBase, StyledDownCaretIconWrapper, StyledMenu } from './ActionsMenu.styles';
import { Button } from '../../transitional/components/Button';
import { IconButton } from '../../transitional/components/IconButton';
import { filterProps } from '../../utils/filterProps';
import { useActionsCollapserContext } from '../../transitional/components/ActionsCollapser/ActionsCollapserProvider';
import { useTabsContext } from '../../transitional/components/TabsTransitionalOld/TabsProvider';
import { useMapTokenPropsToCSS } from '../../hooks/useMapTokenPropsToCSS';
import { deepMerge } from '../../transitional/utils/deepMerge';
import { StyledTabContainer, TitleContainer } from '../../transitional/components/TabsTransitionalOld/Tab';
import { StyledLink } from '../../transitional/components/Link/Link';
import { getDefaultNode } from '../../transitional/utils/RootNode';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledMenuOptionsWrapper = styled(StyledMenuOptionsWrapperBase).withConfig({
  displayName: "ChildrenActionsMenu__StyledMenuOptionsWrapper",
  componentId: "wc5ep8-0"
})(["overflow-x:hidden;"]);
const StyledMenuOption = styled.div.withConfig({
  displayName: "ChildrenActionsMenu__StyledMenuOption",
  componentId: "wc5ep8-1"
})(({
  theme,
  $isActive
}) => {
  const activeStyles = {
    backgroundColor: theme.color['fill-field-hover'],
    color: theme.color['text-core-default'],
    textDecoration: 'none'
  };
  const disabledStyles = {
    backgroundColor: theme.color['fill-transparent-default'],
    color: theme.color['text-primary-disabled'],
    cursor: 'not-allowed'
  };
  const tabStylesOverrides = {
    [`& ${StyledTabContainer}`]: {
      [`&[aria-disabled='true']`]: Object.assign({}, disabledStyles),
      [`& ${StyledLink}`]: {
        display: 'inline-block',
        [`& ${TitleContainer}`]: {
          color: theme.color['text-core-default']
        },
        [`&[aria-disabled='true'] ${TitleContainer}`]: {
          color: theme.color['text-primary-disabled']
        }
      }
    }
  };
  return Object.assign({}, tabStylesOverrides, {
    [`& > :first-child`]: {
      all: 'unset',
      color: theme.color['text-core-default'],
      cursor: 'pointer',
      display: 'inline-block',
      paddingBlock: theme.space[300],
      paddingInline: theme.space[400],
      width: '100%',
      [`&:not([aria-disabled='true'])`]: Object.assign({}, $isActive && activeStyles),
      [`&[aria-disabled='true']`]: Object.assign({}, disabledStyles)
    }
  });
});
export const ChildrenActionsMenu = _ref => {
  var _actionsCollapserCont, _tabsContext$actionsM, _middlewareData$hide;
  let {
      referenceIndicator,
      referenceText,
      referenceSize = 'default',
      referenceTabIndex,
      referenceStyles,
      placement = 'bottom-start',
      disabled,
      menuInlineSize,
      closeOnMenuClick = true,
      open: controlledOpen,
      autoUpdateMenuPosition = true,
      'data-test-id': dataTestId,
      onActionClick = () => {},
      onOpenChange,
      use,
      shape,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isOpen,
    setIsOpen
  } = useControlledOpen({
    controlledOpen,
    onOpenChange
  });
  const [activeIndex, setActiveIndex] = useState(null);
  const listRef = useRef([]);
  const listContentRef = useRef([]);
  const isTypingRef = useRef(false);
  const referenceLayout = getReferenceLayout(referenceIndicator, referenceText);
  const {
    refs,
    context,
    floatingStyles,
    middlewareData
  } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdateMenuPosition ? autoUpdate : undefined,
    middleware: [offset(5), flip({
      padding: 10
    }), shift({
      padding: 10
    }), size({
      apply({
        rects,
        elements
      }) {
        let inlineSize = `${menuInlineSize !== null && menuInlineSize !== void 0 ? menuInlineSize : rects.reference.width}px`;
        if (referenceLayout === 'indicator-only') {
          inlineSize = `${menuInlineSize !== null && menuInlineSize !== void 0 ? menuInlineSize : rects.floating.width}px`;
        }
        Object.assign(elements.floating.style, {
          inlineSize
        });
      },
      padding: 10
    }), hide()]
  });
  const click = useClick(context, {
    event: 'mousedown'
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'listbox'
  });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true,
    focusItemOnOpen: true,
    focusItemOnHover: false
  });
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    onMatch: setActiveIndex,
    enabled: true,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    }
  });
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions([dismiss, role, listNav, typeahead, click]);
  const handleActionClick = useCallback(value => {
    onActionClick({
      value
    });
    if (closeOnMenuClick) {
      setIsOpen(false);
    }
  }, [closeOnMenuClick, onActionClick, setIsOpen]);
  const ReferenceButton = referenceLayout === 'indicator-only' ? IconButton : Button;
  const {
    domProps
  } = filterProps(rest);
  const {
    tokenProps
  } = filterProps(referenceStyles || {});
  const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy
    } = domProps,
    otherDomProps = _objectWithoutPropertiesLoose(domProps, _excluded2);
  const marginStyles = useMapTokenPropsToCSS(tokenProps);
  const actionsCollapserContext = useActionsCollapserContext();
  const tabsContext = useTabsContext();
  const sharedStyleOverrides = deepMerge((_actionsCollapserCont = actionsCollapserContext === null || actionsCollapserContext === void 0 ? void 0 : actionsCollapserContext.actionsMenuStyleOverrides) !== null && _actionsCollapserCont !== void 0 ? _actionsCollapserCont : {}, (_tabsContext$actionsM = tabsContext === null || tabsContext === void 0 ? void 0 : tabsContext.actionsMenuStyleOverrides) !== null && _tabsContext$actionsM !== void 0 ? _tabsContext$actionsM : {});
  return /*#__PURE__*/_jsxs(StyledContainer, Object.assign({}, otherDomProps, {
    $styleOverrides: sharedStyleOverrides,
    children: [/*#__PURE__*/_jsx(StyledReferenceWrapper, {
      $referenceSize: referenceSize,
      $referenceLayout: referenceLayout,
      $marginStyles: marginStyles,
      children: /*#__PURE__*/_jsx(ReferenceButton, Object.assign({
        buttonRef: refs.setReference,
        "aria-label": ariaLabel || referenceText || unescapedText('foundations-components.ActionsMenu.defaultAriaLabel'),
        "aria-labelledby": ariaLabelledBy,
        "data-test-id": dataTestId,
        disabled: disabled,
        use: use,
        size: referenceSize === 'default' ? 'md' : 'sm'
      }, referenceLayout === 'indicator-only' && {
        shape
      }, getReferenceProps(), referenceTabIndex !== undefined ? {
        tabIndex: referenceTabIndex
      } : {}, {
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "center",
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            overflow: "hidden",
            gap: referenceLayout === 'indicator-text' ? '200' : '0',
            children: [referenceIndicator && /*#__PURE__*/_jsx(StyledReferenceIndicator, {
              $referenceSize: referenceSize,
              children: referenceIndicator
            }), referenceText && /*#__PURE__*/_jsx(StyledReferenceText, {
              children: referenceText
            })]
          }), referenceLayout !== 'indicator-only' && /*#__PURE__*/_jsx(StyledDownCaretIconWrapper, {
            $iconSize: "100",
            children: /*#__PURE__*/_jsx(DownCaratIcon, {})
          })]
        })
      }))
    }), isOpen && !disabled && /*#__PURE__*/_jsx(FloatingPortal, {
      root: getDefaultNode(),
      children: /*#__PURE__*/_jsx(FloatingFocusManager, {
        context: context,
        modal: false,
        returnFocus: true,
        children: /*#__PURE__*/_jsx(StyledMenu, Object.assign({
          $isReferenceHidden: (_middlewareData$hide = middlewareData.hide) === null || _middlewareData$hide === void 0 ? void 0 : _middlewareData$hide.referenceHidden,
          ref: refs.setFloating,
          style: floatingStyles
        }, getFloatingProps(), {
          children: /*#__PURE__*/_jsx(StyledMenuOptionsWrapper, {
            children: Children.toArray(children).map((child, index) => {
              const key = typeof child === 'string' ? child : child.key;
              const isActive = activeIndex === index;
              let isValidAction = false;
              return /*#__PURE__*/_jsx(StyledMenuOption, Object.assign({
                $isActive: isActive,
                ref: node => {
                  if (node) {
                    const hasDisabledChild = node.querySelector('[aria-disabled="true"]');
                    if (!hasDisabledChild) {
                      isValidAction = true;
                      listRef.current[index] = node;
                    }
                  }
                },
                role: "option",
                tabIndex: -1
              }, getItemProps({
                onClick() {
                  if (isValidAction) {
                    handleActionClick(key);
                  }
                },
                onKeyDown(event) {
                  const isEnter = event.key === 'Enter';
                  const isSpaceNotTyping = event.key === ' ' && !isTypingRef.current;
                  if (isEnter || isSpaceNotTyping) {
                    event.preventDefault();
                    if (isValidAction) {
                      handleActionClick(key);
                    }
                  }
                },
                onPointerEnter() {
                  setActiveIndex(index);
                },
                onPointerLeave() {
                  setActiveIndex(null);
                }
              }), {
                children: child
              }), index);
            })
          })
        }))
      })
    })]
  }));
};